import React, { useState, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { ValidationRule, ButtonType } from 'devextreme-react/common';
import { Popup } from 'devextreme-react/popup';
import { PopupPosition } from '../common/PopupPositions';
import { Form, Label, FormRef, GroupItem, SimpleItem, ButtonItem, EmptyItem } from 'devextreme-react/form';
import { LoadPanel } from 'devextreme-react/load-panel';

import { formatMessage } from 'devextreme/localization';

import DatabaseApi from '../../api/DatabaseApi';

import LabelTemplate from '../label-template/LabelTemplate';

import DatabaseFormDto from '../../classes/dtos/datastructuremodule/DatabaseFormDto';

import { JobStatusError } from '../../classes/consts/JobStatus';

interface DatabaseFormProps {
    DatabaseAdded(name: string): void;
}

export interface DatabaseFormHandle {
    Open: (dataSourceId: number) => void;
}

const DatabaseForm = forwardRef<DatabaseFormHandle, DatabaseFormProps>(({ DatabaseAdded }, ref) => {

    const position = { of: '#root' };

    const formRef = useRef<FormRef>(null);

    const [dataSourceId, setDataSourceId] = useState<number>(-1);

    const [databaseFormDto, setDatabaseFormDto] = useState<DatabaseFormDto>(new DatabaseFormDto(null));

    const [isOpen, setIsOpen] = useState(false);

    const [saveInProgress, setSaveInProgress] = useState(false);

    useImperativeHandle(ref, () => ({
        Open(dataSourceId: number) {
            setDataSourceId(dataSourceId);
            setDatabaseFormDto(new DatabaseFormDto(null));
            setIsOpen(true);
        }
    }));

    const databaseNameEditorOptions = {
        valueChangeEvent: 'keyup',
        required: true
    };

    const validationRules: {
        databaseName: ValidationRule[],
    } = {
        databaseName: [
            { type: 'required', message: 'Database name is required.' },
        ]
    };

    const close = () => {
        setIsOpen(false);
    };

    const popupAttributes = useMemo(() => {
        return {
            id: 'elementId',
            class: 'class-name-popup'
        };
    }, []);

    const handleSubmit = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();

        if (databaseFormDto.Name === null || databaseFormDto.Name.trim() === "") {
            return;
        }

        setSaveInProgress(true);

        let result = await DatabaseApi.CreateDatabaseAsync(dataSourceId, databaseFormDto.Name.trim());

        if (result.IsFailed()) {
            alert("Error creating database: " + result.ErrorMessage);
            setSaveInProgress(false);
            return;
        }

        if (result.Result.Accepted === false) {
            alert("Error creating database: " + formatMessage('DatabaseForm_NotAccepted'));
            setSaveInProgress(false);
            return;
        }

        if (result.Result.Status === JobStatusError) {
            alert("Error creating database: " + result.Result.ErrorMessage);
            setSaveInProgress(false);
            return;
        }

        DatabaseAdded(databaseFormDto.Name.trim());
        setIsOpen(false);
        setSaveInProgress(false);
    };

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'outlined' as ButtonType,
        useSubmitBehavior: false,
        width: '100%',
        onClick: close
    };

    const confirmButtonOptions = {
        text: 'Confirm',
        type: 'default' as ButtonType,
        useSubmitBehavior: true,
        width: '100%'
    };

    return (
        <React.Fragment>
            <Popup
                wrapperAttr={popupAttributes}
                visible={isOpen}
                onHiding={close}
                dragEnabled={false}
                hideOnOutsideClick={false}
                showCloseButton={true}
                showTitle={true}
                title={formatMessage('DatabaseForm_TitleCreate')}
                container=".dx-viewport"
                width={600}
                height="auto"
                position={PopupPosition}
            >
                <form onSubmit={handleSubmit}>
                    <Form
                        ref={formRef}
                        formData={databaseFormDto}
                        showColonAfterLabel={true}
                        showValidationSummary={false}
                        validationGroup="DatabaseDtoData"
                        colCount={2}
                        disabled={saveInProgress}
                    >
                        <GroupItem colSpan={2}>
                            <SimpleItem dataField="Name" editorOptions={databaseNameEditorOptions} validationRules={validationRules.databaseName}>
                                <Label text={"Database name"} render={LabelTemplate('rename')} />
                            </SimpleItem>
                        </GroupItem>
                        <EmptyItem colSpan={2} />
                        <GroupItem>
                            <ButtonItem buttonOptions={cancelButtonOptions} />
                        </GroupItem>
                        <GroupItem>
                            <ButtonItem buttonOptions={confirmButtonOptions} />
                        </GroupItem>
                    </Form>
                </form>
            </Popup>
            <LoadPanel
                position={position}
                shadingColor="rgba(0,0,0,0.4)"
                visible={saveInProgress}
            />
        </React.Fragment>
    );
});

export default DatabaseForm;
